import { useState, useRef, useEffect } from "react";
import { useMap } from "@vis.gl/react-google-maps";

import GoogleMap from "@shared/v2/GoogleMap/GoogleMap";
import "./map.scss";
import CustomOverlay from "../../../SearchListings/components/CustomOverlay";
import PropertyCard from "./PropertyCard";
import { formatSimplePrice } from "../../../SearchListings/components/helpers";
import dayjs from "@shared/dayjs";
import ResultsCount from "../ResultsCount";

const Map = ({
  searchConfig,
  locationQuery,
  setLocationQuery,
  properties,
  radius,
  onSelect,
  selected,
  onClickProperty,
  addProspectAsContact,
  callProspect,
  setPropertyForDetails,
  areaGeometry,
  resultsCount,
  limit,
}) => {
  const container = useRef(null);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const onPolygonDrawn = (feature) => {
    if (!feature?.geometry?.coordinates?.[0]?.length) {
      setLocationQuery(null);
      return;
    }

    const polygon = feature.geometry.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
    if (polygon.length > 1) {
      const first = polygon[0];
      const last = polygon[polygon.length - 1];

      if (first.lat === last.lat && first.lng === last.lng) {
        polygon.pop();
      }
    }

    setLocationQuery({ isRadius: false, polygon });
  };

  const onClickMarker = (index) => {
    if (index === selectedProperty) {
      deselectProperty();
      return;
    }

    setSelectedProperty(index);
    onClickProperty(properties[index].id);
  };

  const deselectProperty = () => {
    setSelectedProperty(null);
    onClickProperty(null);
  };

  useEffect(() => {
    deselectProperty();
  }, [locationQuery]);

  return (
    <div ref={container} className="tw-relative tw-h-full">
      <GoogleMap
        onChange={onPolygonDrawn}
        defaultPolygon={locationQuery?.polygon}
        mapProps={{
          className: "tw-w-full tw-h-full tw-rounded-[8px] tw-overflow-hidden opportunities-map",
          defaultCenter: { lat: 48.45, lng: -122.29 },
          defaultZoom: 11,
          mapTypeId: "roadmap",
          disableDefaultUI: true,
          clickableIcons: false,
          onClick: deselectProperty,
        }}
      >
        <MapViewportManager locationQuery={locationQuery} radius={radius} areaGeometry={areaGeometry} />
        {properties?.map((property, i) => (
          <CustomOverlay
            key={property.id}
            position={{ lat: property.lat, lng: property.lng }}
            pixelOffset={{ x: -32, y: -34 }}
            zIndex={selectedProperty === i ? 10 : "initial"}
          >
            <PropertyMarker
              value={
                searchConfig.typeName === "Preforeclosure"
                  ? dayjs(property.statusDate).format("M/D")
                  : formatSimplePrice(property.listPrice) || "N/A"
              }
              price={property.listPrice}
              date={property.statusDate}
              onClick={() => onClickMarker(i)}
              isSelected={selectedProperty === i}
            />
          </CustomOverlay>
        ))}
        {properties?.length > 0 && selectedProperty !== null && properties[selectedProperty] && (
          <CustomOverlay
            key={properties[selectedProperty].id}
            position={{ lat: properties[selectedProperty].lat, lng: properties[selectedProperty].lng }}
            pixelOffset={{ x: -150, y: -324 }}
            allowReposition={true}
            elementSize={{ width: 279, height: 284 }}
          >
            <PropertyCard
              config={searchConfig}
              property={properties[selectedProperty]}
              isSelected={selected.includes(properties[selectedProperty].id)}
              select={() => onSelect(properties[selectedProperty])}
              addProspectAsContact={addProspectAsContact}
              callProspect={callProspect}
              setPropertyForDetails={setPropertyForDetails}
            />
          </CustomOverlay>
        )}
      </GoogleMap>
      {resultsCount > 0 && (
        <div className="tw-absolute tw-bottom-12px tw-left-1/2 tw--translate-x-1/2 tw-bg-white tw-rounded-[8px] tw-border tw-border-solid tw-p-12px tw-border-gray-10 tw-shadow-[0_2px_8px_0_rgba(213,213,213,0.5)]">
          <ResultsCount resultCount={resultsCount} limit={limit} className="tw-text-gray-75" />
        </div>
      )}
    </div>
  );
};

export default Map;

const baseShapeStyle = {
  strokeColor: "#666666",
  strokeWeight: 2,
  fillColor: "#000000",
  fillOpacity: 0.1,
  zIndex: 1,
};

const MapViewportManager = ({ locationQuery, radius, areaGeometry }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) {
      return;
    }

    let shape;
    map.data.forEach((feature) => map.data.remove(feature));

    if (!locationQuery) {
      return;
    }

    if (locationQuery.isRadius) {
      shape = new window.google.maps.Circle({
        ...baseShapeStyle,
        map,
        center: locationQuery.location,
        radius,
        clickable: false,
      });

      map.fitBounds(shape.getBounds());
    } else if (locationQuery.isAddressSearch) {
      map.setCenter(locationQuery.location);
      map.setZoom(16);
    } else if (locationQuery.isAreaSearch && areaGeometry) {
      map.data.addGeoJson({
        type: "Feature",
        geometry: areaGeometry,
      });

      map.data.setStyle({
        ...baseShapeStyle,
        editable: false,
        draggable: false,
        clickable: false,
      });

      const bounds = new window.google.maps.LatLngBounds();
      map.data.forEach((f) => {
        f.getGeometry().forEachLatLng((p) => {
          bounds.extend(p);
        });
      });

      map.fitBounds(bounds);
    } else if (locationQuery.polygon?.length) {
      shape = new window.google.maps.Polygon({
        ...baseShapeStyle,
        map,
        paths: locationQuery.polygon,
        editable: false,
        draggable: false,
        clickable: false,
      });

      const bounds = new window.google.maps.LatLngBounds();
      locationQuery.polygon.forEach((p) => {
        bounds.extend(p);
      });

      map.fitBounds(bounds);
    }

    return () => {
      shape?.setMap(null);
      map.data.forEach((feature) => map.data.remove(feature));
    };
  }, [map, locationQuery, radius, areaGeometry]);

  return null;
};

const PropertyMarker = ({ value, onClick, isSelected }) => {
  const clicks = (e) => {
    e.preventDefault();

    onClick();
  };

  const selectedStyling = isSelected
    ? "tw-border-theme-primary tw-text-theme-primary before:tw-border-t-theme-primary"
    : "tw-border-gray-30 tw-text-gray-75 before:tw-border-t-gray-30";

  const stateStyles = `
    before:tw-content-[''] before:tw-block before:tw-absolute before:tw-h-0 before:tw-w-0 before:tw-border-solid before:tw-border-transparent
    after:tw-content-[''] after:tw-block after:tw-absolute after:tw-h-0 after:tw-w-0 after:tw-border-solid after:tw-border-transparent
    before:tw-top-full before:tw-border-[7px]
    after:tw-top-[95%] after:tw-border-[7px] after:tw-border-t-white
    hover:tw-border-theme-primary hover:tw-text-theme-primary hover:tw-bg-theme-primary-disabled
    hover:before:tw-border-t-theme-primary hover:after:tw-border-t-theme-primary-disabled
    active:tw-border-theme-primary active:tw-text-white active:tw-bg-theme-primary
    active:before:tw-border-t-theme-primary active:after:tw-border-t-theme-primary
  `;

  return (
    <div
      onClick={clicks}
      className={`tw-relative tw-flex tw-cursor-pointer tw-justify-center tw-bg-white tw-px-8px tw-py-3px tw-items-center tw-rounded-666px tw-border-1px tw-border-solid ${stateStyles} ${selectedStyling}`}
    >
      <span className="tw-text-14px tw-font-sans tw-font-semibold">{value}</span>
    </div>
  );
};
